import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Info,
  Database,
  RefreshCw,
  Download,
  HelpCircle,
  BookOpen,
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, Cell, AreaChart, Area, LineChart, Line
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/credit-portfolio`;

// Format helpers
const formatCurrency = (value, short = true) => {
  if (short) {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const getStatusIcon = (status) => {
  switch (status) {
    case "good": return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
    case "watch": return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    case "risk": return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
    default: return null;
  }
};

const RISK_CLASS_COLORS = {
  1: "#16A34A", 2: "#65A30D", 3: "#EAB308", 4: "#EA580C", 5: "#DC2626"
};

const RISK_CLASS_LABELS = {
  1: "Lowest Risk", 2: "Low Risk", 3: "Moderate Risk", 4: "Elevated Risk", 5: "Highest Risk"
};

export default function CreditPortfolioDashboard() {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [glossary, setGlossary] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAnalystView, setIsAnalystView] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showGlossary, setShowGlossary] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showMoreMetrics, setShowMoreMetrics] = useState(false);
  const [glossarySearch, setGlossarySearch] = useState("");

  const [segmentFilter, setSegmentFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [riskClassFilter, setRiskClassFilter] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, metricsRes, glossaryRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/metrics`),
        axios.get(`${API}/glossary`)
      ]);
      setData(dashboardRes.data);
      setMetrics(metricsRes.data);
      setGlossary(glossaryRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredGlossary = Object.entries(glossary).filter(([term]) => 
    term.toLowerCase().includes(glossarySearch.toLowerCase())
  );

  // Calculate percentages
  const grossExposure = data?.kpis?.gross_exposure?.value || 1;
  const calcPct = (val) => ((val / grossExposure) * 100).toFixed(1);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-[#502D3C]" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC]">
        {/* Compact Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-[#502D3C] tracking-tight">Credit Portfolio Health</h1>
              <Badge variant="outline" className="text-[10px] font-normal bg-gray-50 h-5">Corporate Card • Non-Revolving</Badge>
              <Badge variant="outline" className="text-[10px] bg-white h-5">{data?.as_of_date}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowExplain(true)} className="text-[#64748B] hover:text-[#502D3C] h-8 text-xs">
                <HelpCircle className="h-3.5 w-3.5 mr-1" />Guide
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowGlossary(true)} className="text-[#64748B] hover:text-[#502D3C] h-8 text-xs">
                <BookOpen className="h-3.5 w-3.5 mr-1" />Glossary
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs"><Download className="h-3.5 w-3.5 mr-1" />Export</Button>
              <Button variant="ghost" size="icon" onClick={fetchData} className="h-8 w-8">
                <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
              </Button>
              <div className="ml-2 flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded text-[11px]">
                <span className={!isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Simple</span>
                <Switch checked={isAnalystView} onCheckedChange={setIsAnalystView} className="scale-75" />
                <span className={isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Analyst</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={segmentFilter} onValueChange={setSegmentFilter}>
              <SelectTrigger className="w-[110px] h-7 bg-white text-[11px] border-gray-200"><SelectValue placeholder="Segment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Segments</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                <SelectItem value="mid-market">Mid-Market</SelectItem>
                <SelectItem value="smb">SMB</SelectItem>
              </SelectContent>
            </Select>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-[110px] h-7 bg-white text-[11px] border-gray-200"><SelectValue placeholder="Industry" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                <SelectItem value="technology">Technology</SelectItem>
                <SelectItem value="manufacturing">Manufacturing</SelectItem>
                <SelectItem value="services">Services</SelectItem>
              </SelectContent>
            </Select>
            <Select value={riskClassFilter} onValueChange={setRiskClassFilter}>
              <SelectTrigger className="w-[100px] h-7 bg-white text-[11px] border-gray-200"><SelectValue placeholder="Risk" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                <SelectItem value="1">Lowest Risk</SelectItem>
                <SelectItem value="2">Low Risk</SelectItem>
                <SelectItem value="3">Moderate Risk</SelectItem>
                <SelectItem value="4">Elevated Risk</SelectItem>
                <SelectItem value="5">Highest Risk</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative max-w-[180px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[#94A3B8]" />
              <Input placeholder="Search..." className="pl-7 h-7 bg-white text-[11px] border-gray-200" />
            </div>
          </div>
        </header>

        <main className="p-4 space-y-4">
          {/* A. Executive Summary Banner */}
          <Card className="bg-[#502D3C] text-white border-0" data-testid="executive-summary">
            <CardContent className="p-0">
              <div className="grid grid-cols-12">
                <div className="col-span-5 p-4 border-r border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9px] font-semibold text-white/50 uppercase tracking-widest">Briefing</span>
                    <Badge className="text-[9px] h-4 bg-amber-500/20 text-amber-300 border-amber-400/30">Needs Attention</Badge>
                  </div>
                  <div className="space-y-2">
                    {data?.executive_summary?.takeaways?.map((t, i) => (
                      <p key={i} className="text-[12px] text-white/90 flex items-start gap-2 leading-relaxed">
                        <span className="text-[9px] font-bold text-[#FFCC1C] mt-0.5">{i + 1}.</span>{t}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="col-span-4 p-4 bg-red-500/10 border-r border-white/10">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="h-3 w-3 text-red-400" />
                    <span className="text-[9px] font-semibold text-red-300 uppercase tracking-widest">Risk Alert</span>
                  </div>
                  <p className="text-[12px] text-white/90 leading-relaxed">{data?.executive_summary?.risk_alert}</p>
                </div>
                <div className="col-span-3 p-4 bg-[#FFCC1C]/10">
                  <div className="flex items-center gap-1.5 mb-2">
                    <ChevronRight className="h-3 w-3 text-[#FFCC1C]" />
                    <span className="text-[9px] font-semibold text-[#FFCC1C] uppercase tracking-widest">Next Step</span>
                  </div>
                  <p className="text-[12px] text-white/90 leading-relaxed">{data?.executive_summary?.where_to_look}</p>
                </div>
              </div>
              <div className="px-4 py-2 bg-black/20 border-t border-white/5">
                <p className="text-[10px] text-white/40">{data?.executive_summary?.confidence_note}</p>
              </div>
            </CardContent>
          </Card>

          {/* B. Primary KPIs - Operator's Briefing Layout */}
          <div className="grid grid-cols-12 gap-3">
            {/* Portfolio Size - Context */}
            <div className="col-span-2">
              <Card className="bg-gray-50 border-gray-100 h-full">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[9px] font-medium text-[#64748B] uppercase tracking-wider">Portfolio Size</p>
                    <SourceIcon tooltip={metrics?.gross_exposure?.source_system} small />
                  </div>
                  <p className="text-2xl font-bold text-[#502D3C] tracking-tight">{data?.kpis?.gross_exposure?.formatted_value}</p>
                  <p className="text-[9px] text-[#94A3B8] mt-1">Total money owed to Rippling</p>
                </CardContent>
              </Card>
            </div>

            {/* Core Health Indicators - 4 Key Metrics */}
            <div className="col-span-8">
              <div className="bg-gradient-to-br from-slate-100 via-sky-100/60 to-indigo-100/40 border border-slate-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-4 bg-[#FFCC1C] rounded-full" />
                  <span className="text-[10px] font-semibold text-[#502D3C] uppercase tracking-wider">Core Health Indicators</span>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <CoreHealthCard 
                    metric={data?.kpis?.past_due}
                    metricInfo={metrics?.past_due}
                    title="Past Due"
                    pctOfExposure={calcPct(data?.kpis?.past_due?.value)}
                    interpretation="Money already late"
                    alert={data?.kpis?.past_due?.status === "watch" || data?.kpis?.past_due?.status === "risk"}
                    onInfoClick={() => setSelectedMetric("past_due")}
                  />
                  <CoreHealthCard 
                    metric={data?.kpis?.due_next_30}
                    metricInfo={metrics?.due_next_30}
                    title="Due in 30 Days"
                    pctOfExposure={calcPct(data?.kpis?.due_next_30?.value)}
                    interpretation="Upcoming collections"
                    onInfoClick={() => setSelectedMetric("due_next_30")}
                  />
                  <CoreHealthCard 
                    metric={data?.kpis?.on_time_collection}
                    metricInfo={metrics?.on_time_collection}
                    title="On-Time Collection"
                    interpretation="Payment reliability"
                    isRate
                    onInfoClick={() => setSelectedMetric("on_time_collection")}
                  />
                  <CoreHealthCard 
                    metric={data?.kpis?.ecl}
                    metricInfo={metrics?.ecl}
                    title="Expected Credit Loss"
                    pctOfExposure={calcPct(data?.kpis?.ecl?.value)}
                    interpretation="Predicted future loss"
                    alert={data?.kpis?.ecl?.status === "watch" || data?.kpis?.ecl?.status === "risk"}
                    onInfoClick={() => setSelectedMetric("ecl")}
                  />
                </div>
              </div>
            </div>

            {/* Realized Loss - Outcome */}
            <div className="col-span-2">
              <Card className="bg-gray-50 border-gray-100 h-full">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[9px] font-medium text-[#64748B] uppercase tracking-wider">Realized Loss</p>
                    <SourceIcon tooltip={metrics?.net_writeoffs?.source_system} small />
                  </div>
                  <p className="text-2xl font-bold text-[#502D3C] tracking-tight">{data?.kpis?.net_writeoffs?.formatted_value}</p>
                  <p className="text-[9px] text-[#94A3B8] mt-0.5">({calcPct(data?.kpis?.net_writeoffs?.value)}% of book)</p>
                  <p className="text-[9px] text-green-600 mt-1 flex items-center gap-0.5">
                    <TrendingDown className="h-2.5 w-2.5" />
                    {Math.abs(data?.kpis?.net_writeoffs?.change_pct)}% vs last month
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* More Portfolio Metrics (Collapsible) */}
          <Collapsible open={showMoreMetrics} onOpenChange={setShowMoreMetrics}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-center text-[11px] text-[#64748B] hover:text-[#502D3C] h-7 gap-1">
                {showMoreMetrics ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {showMoreMetrics ? "Hide additional metrics" : "Show 6 more portfolio metrics"}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3">
              <div className="grid grid-cols-6 gap-3">
                <KPICardSecondary metric={data?.kpis?.current_not_due} title="Current / Not Due" pctOfExposure={calcPct(data?.kpis?.current_not_due?.value)} onInfoClick={() => setSelectedMetric("current_not_due")} />
                <KPICardSecondary metric={data?.kpis?.due_next_7} title="Due in Next 7 Days" pctOfExposure={calcPct(data?.kpis?.due_next_7?.value)} onInfoClick={() => setSelectedMetric("due_next_7")} />
                <KPICardSecondary metric={data?.kpis?.first_attempt_success} title="First-Attempt Success" onInfoClick={() => setSelectedMetric("first_attempt_success")} />
                <KPICardSecondary metric={data?.kpis?.weighted_avg_dpd} title="Weighted Avg DPD" onInfoClick={() => setSelectedMetric("weighted_avg_dpd")} />
                <KPICardSecondary metric={data?.kpis?.ecl_rate} title="ECL Rate" onInfoClick={() => setSelectedMetric("ecl_rate")} />
                <KPICardSecondary metric={data?.kpis?.watchlist_exposure} title="Watchlist Exposure" pctOfExposure={calcPct(data?.kpis?.watchlist_exposure?.value)} onInfoClick={() => setSelectedMetric("watchlist_exposure")} />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* C. Collections and Due Profile Row */}
          <div className="grid grid-cols-12 gap-4">
            <Card className="col-span-5">
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">Collections Funnel</CardTitle>
                  <SourceIcon tooltip="finance.card_collection_attempt" />
                </div>
                <p className="text-[10px] text-[#64748B]">What happened to money that came due this period</p>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <CollectionsFunnel data={data?.collections_funnel} />
              </CardContent>
            </Card>

            <Card className="col-span-4">
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">Portfolio by Risk Class</CardTitle>
                  <SourceIcon tooltip="risk.card_risk_class_daily" />
                </div>
                <p className="text-[10px] text-[#64748B]">Higher class = higher risk</p>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <RiskDistributionChart data={data?.risk_distribution} />
              </CardContent>
            </Card>

            <Card className="col-span-3">
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">30-Day Migration</CardTitle>
                  <SourceIcon tooltip="risk.card_risk_class_history" />
                </div>
                <p className="text-[10px] text-[#64748B]">How risk changed</p>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <MigrationSummary data={data?.migration_30d} />
              </CardContent>
            </Card>
          </div>

          {/* D. Trends Row */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">Exposure Trend</CardTitle>
                  <SourceIcon tooltip="finance.card_receivable_daily" />
                </div>
                <p className="text-[10px] text-[#64748B]">How total portfolio size has changed over 12 months</p>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <ExposureTrendChart data={data?.trends} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">ECL & Write-offs Trend</CardTitle>
                  <SourceIcon tooltip="risk.card_expected_loss_daily" />
                </div>
                <p className="text-[10px] text-[#64748B]">Expected loss vs realized loss over time</p>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <LossTrendChart data={data?.trends} />
              </CardContent>
            </Card>
          </div>

          {/* E. Concentration and Worsening Accounts */}
          <div className="grid grid-cols-12 gap-4">
            <Card className="col-span-7">
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">Top Worsening Accounts</CardTitle>
                  <SourceIcon tooltip="risk.card_watchlist_daily" />
                </div>
                <p className="text-[10px] text-[#64748B]">Accounts requiring immediate attention</p>
              </CardHeader>
              <CardContent className="p-0">
                <WorseningAccountsTable data={data?.worsening_accounts} onRowClick={setSelectedCustomer} />
              </CardContent>
            </Card>

            <div className="col-span-5 space-y-4">
              <Card>
                <CardHeader className="pb-1 pt-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#502D3C]">Top-10 Customer Concentration</CardTitle>
                    <SourceIcon tooltip="core.company_dim" />
                  </div>
                  <p className="text-[10px] text-[#64748B]">
                    Top 10 = {data?.top_customers?.reduce((s, c) => s + c.percentage, 0).toFixed(1)}% of exposure
                  </p>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <CustomerConcentrationChart data={data?.top_customers?.slice(0, 5)} onCustomerClick={setSelectedCustomer} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1 pt-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#502D3C]">Industry Concentration</CardTitle>
                    <SourceIcon tooltip="core.industry_dim" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <IndustryConcentrationMini data={data?.top_industries?.slice(0, 4)} />
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        {/* Metric Detail Sheet */}
        <Sheet open={!!selectedMetric} onOpenChange={() => setSelectedMetric(null)}>
          <SheetContent className="w-[420px]">
            <SheetHeader>
              <SheetTitle>{metrics[selectedMetric]?.display_name}</SheetTitle>
              <SheetDescription>{metrics[selectedMetric]?.plain_english_name}</SheetDescription>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-100px)] mt-4">
              <MetricDetailContent metric={metrics[selectedMetric]} isAnalystView={isAnalystView} />
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Glossary Sheet */}
        <Sheet open={showGlossary} onOpenChange={setShowGlossary}>
          <SheetContent className="w-[380px]">
            <SheetHeader>
              <SheetTitle>Glossary</SheetTitle>
              <SheetDescription>Plain-English definitions</SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#64748B]" />
                <Input placeholder="Search..." value={glossarySearch} onChange={(e) => setGlossarySearch(e.target.value)} className="pl-8 h-8 text-sm" />
              </div>
              <ScrollArea className="h-[calc(100vh-180px)]">
                <div className="space-y-3">
                  {filteredGlossary.map(([term, def]) => (
                    <div key={term} className="pb-2 border-b border-[#E2E8F0]">
                      <p className="font-medium text-[#502D3C] text-sm capitalize">{term.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-[#64748B] mt-0.5">{def}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </SheetContent>
        </Sheet>

        {/* Explain Sheet */}
        <Sheet open={showExplain} onOpenChange={setShowExplain}>
          <SheetContent className="w-[420px]">
            <SheetHeader>
              <SheetTitle>Understanding This Dashboard</SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-100px)] mt-4">
              <ExplainContent />
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Customer Detail Sheet */}
        <Sheet open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
          <SheetContent className="w-[450px]">
            <SheetHeader>
              <SheetTitle>{selectedCustomer?.company_name}</SheetTitle>
              <SheetDescription>{selectedCustomer?.industry} • {selectedCustomer?.company_id}</SheetDescription>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-100px)] mt-4">
              <CustomerDetailContent customer={selectedCustomer} />
            </ScrollArea>
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}

// Core Health Card (prominent, larger)
function CoreHealthCard({ metric, metricInfo, title, pctOfExposure, interpretation, alert, isRate, onInfoClick }) {
  if (!metric) return null;
  const changeIcon = metric.change_direction === "up" ? <TrendingUp className="h-2.5 w-2.5" /> : metric.change_direction === "down" ? <TrendingDown className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />;
  const isPositive = metricInfo?.good_direction === "higher_is_better" ? metric.change_direction === "up" : metric.change_direction === "down";
  const changeColor = alert ? "text-amber-600" : isPositive ? "text-green-600" : "text-[#64748B]";

  return (
    <div className={cn(
      "p-3 rounded-lg border-2 transition-all",
      alert ? "bg-amber-50/50 border-amber-200" : "bg-white border-gray-100 hover:border-[#FFCC1C]/50"
    )}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {alert && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
          <p className="text-[10px] font-semibold text-[#502D3C]">{title}</p>
        </div>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={onInfoClick} className="p-0.5 hover:bg-gray-100 rounded opacity-60 hover:opacity-100">
                <Info className="h-3 w-3 text-[#94A3B8]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top"><p className="text-[10px]">View details</p></TooltipContent>
          </Tooltip>
          <SourceIcon tooltip={metricInfo?.source_system} small />
        </div>
      </div>
      
      <div className="flex items-baseline gap-1.5 mb-1">
        <p className={cn(
          "text-2xl font-bold tracking-tight",
          alert ? "text-amber-700" : "text-[#502D3C]"
        )}>{metric.formatted_value}</p>
        {pctOfExposure && (
          <span className={cn(
            "text-[11px] font-medium",
            alert ? "text-amber-600" : "text-[#64748B]"
          )}>({pctOfExposure}%)</span>
        )}
      </div>
      
      <div className="flex items-center gap-1.5 mb-2">
        <span className={cn("flex items-center gap-0.5 text-[10px] font-medium", changeColor)}>
          {changeIcon}{Math.abs(metric.change_pct)}%
        </span>
        <span className="text-[9px] text-[#94A3B8]">vs last month</span>
      </div>
      
      <p className="text-[9px] text-[#64748B] leading-relaxed border-t border-gray-100 pt-2">{interpretation}</p>
    </div>
  );
}

// Primary KPI Card (larger, more prominent)
function KPICardPrimary({ metric, metricInfo, title, pctOfExposure, interpretation, onInfoClick, isAnalystView }) {
  if (!metric) return null;
  const changeIcon = metric.change_direction === "up" ? <TrendingUp className="h-2.5 w-2.5" /> : metric.change_direction === "down" ? <TrendingDown className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />;
  const isGood = metric.status === "good";
  const changeColor = metric.status === "risk" ? "text-red-600" : metric.status === "watch" ? "text-amber-600" : "text-green-600";

  return (
    <Card className="bg-white border-gray-100 hover:shadow-sm transition-shadow">
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-1">
          <p className="text-[10px] font-medium text-[#64748B] leading-tight pr-2">{title}</p>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {getStatusIcon(metric.status)}
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onInfoClick} className="p-0.5 hover:bg-gray-100 rounded opacity-50 hover:opacity-100">
                  <Info className="h-3 w-3 text-[#94A3B8]" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top"><p className="text-[10px]">View details</p></TooltipContent>
            </Tooltip>
            <SourceIcon tooltip={metricInfo?.source_system} small />
          </div>
        </div>
        <div className="flex items-baseline gap-1.5">
          <p className="text-xl font-semibold text-[#502D3C] tracking-tight">{metric.formatted_value}</p>
          {pctOfExposure && <span className="text-[10px] text-[#94A3B8]">({pctOfExposure}%)</span>}
        </div>
        <div className="flex items-center gap-1 mt-1">
          <span className={cn("flex items-center gap-0.5 text-[10px] font-medium", changeColor)}>
            {changeIcon}{Math.abs(metric.change_pct)}%
          </span>
          <span className="text-[10px] text-[#94A3B8]">vs last month</span>
        </div>
        <p className="text-[9px] text-[#94A3B8] mt-1.5 leading-relaxed">{interpretation}</p>
        {isAnalystView && metricInfo?.formula_display && (
          <p className="text-[8px] text-[#94A3B8] mt-1 font-mono bg-gray-50 px-1.5 py-0.5 rounded truncate">{metricInfo.formula_display}</p>
        )}
      </CardContent>
    </Card>
  );
}

// Secondary KPI Card (smaller, in collapsed section)
function KPICardSecondary({ metric, title, pctOfExposure, onInfoClick }) {
  if (!metric) return null;
  return (
    <Card className="bg-gray-50 border-gray-100">
      <CardContent className="p-2.5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[9px] font-medium text-[#64748B]">{title}</p>
          <button onClick={onInfoClick} className="p-0.5 hover:bg-gray-200 rounded">
            <Info className="h-2.5 w-2.5 text-[#94A3B8]" />
          </button>
        </div>
        <div className="flex items-baseline gap-1">
          <p className="text-base font-semibold text-[#502D3C]">{metric.formatted_value}</p>
          {pctOfExposure && <span className="text-[9px] text-[#94A3B8]">({pctOfExposure}%)</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// Source Icon with tooltip and click behavior
function SourceIcon({ tooltip, small = false }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button className={cn("hover:bg-gray-100 rounded opacity-50 hover:opacity-100", small ? "p-0.5" : "p-1")}>
          <Database className={cn("text-[#94A3B8]", small ? "h-2.5 w-2.5" : "h-3.5 w-3.5")} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p className="text-[10px] font-mono">{tooltip || "Rippling Data"}</p>
        <p className="text-[9px] text-gray-400">Click for lineage</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Collections Funnel
function CollectionsFunnel({ data }) {
  if (!data) return null;
  const colors = ['#502D3C', '#FFCC1C', '#16A34A', '#DC2626'];
  return (
    <div className="space-y-2">
      {data.map((stage, i) => (
        <div key={stage.stage}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[11px] font-medium text-[#502D3C]">{stage.stage}</span>
            <span className="text-[10px] text-[#64748B]">{formatCurrency(stage.amount)} <span className="text-[#94A3B8]">({stage.percentage}%)</span></span>
          </div>
          <div className="h-5 bg-gray-100 rounded overflow-hidden">
            <div className="h-full rounded flex items-center justify-end pr-2 transition-all" style={{ width: `${stage.percentage}%`, backgroundColor: colors[i] }}>
              {stage.drop_off_pct && <span className="text-[9px] text-white font-medium">-{stage.drop_off_pct}%</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Risk Distribution Chart
function RiskDistributionChart({ data }) {
  if (!data) return null;
  return (
    <div className="h-[160px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 80, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal vertical={false} />
          <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 9 }} />
          <YAxis type="category" dataKey="class_label" tick={{ fontSize: 9 }} width={85} />
          <RechartsTooltip formatter={(v) => [formatCurrency(v, false), "Exposure"]} contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 11 }} />
          <Bar dataKey="exposure" radius={[0, 3, 3, 0]}>
            {data.map((e) => <Cell key={e.risk_class} fill={RISK_CLASS_COLORS[e.risk_class]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Migration Summary
function MigrationSummary({ data }) {
  if (!data) return null;
  const improved = data.filter(m => m.to_class < m.from_class).reduce((s, m) => s + m.exposure, 0);
  const stable = data.filter(m => m.to_class === m.from_class).reduce((s, m) => s + m.exposure, 0);
  const worsened = data.filter(m => m.to_class > m.from_class).reduce((s, m) => s + m.exposure, 0);
  const total = improved + stable + worsened;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center p-2 bg-green-50 rounded">
          <p className="text-sm font-bold text-green-700">{((improved/total)*100).toFixed(0)}%</p>
          <p className="text-[9px] text-green-600">Improved</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <p className="text-sm font-bold text-gray-700">{((stable/total)*100).toFixed(0)}%</p>
          <p className="text-[9px] text-gray-600">Stable</p>
        </div>
        <div className="text-center p-2 bg-red-50 rounded">
          <p className="text-sm font-bold text-red-700">{((worsened/total)*100).toFixed(0)}%</p>
          <p className="text-[9px] text-red-600">Worsened</p>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
        <div className="bg-green-500 h-full" style={{ width: `${(improved/total)*100}%` }} />
        <div className="bg-gray-400 h-full" style={{ width: `${(stable/total)*100}%` }} />
        <div className="bg-red-500 h-full" style={{ width: `${(worsened/total)*100}%` }} />
      </div>
    </div>
  );
}

// Exposure Trend Chart (simplified)
function ExposureTrendChart({ data }) {
  if (!data) return null;
  return (
    <div className="h-[140px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ left: 0, right: 10, top: 5, bottom: 0 }}>
          <defs>
            <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FFCC1C" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#FFCC1C" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="date" tick={{ fontSize: 9 }} />
          <YAxis tickFormatter={(v) => `$${(v/1_000_000).toFixed(0)}M`} tick={{ fontSize: 9 }} />
          <RechartsTooltip formatter={(v) => [formatCurrency(v, false), "Exposure"]} contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 11 }} />
          <Area type="monotone" dataKey="gross_exposure" stroke="#FFCC1C" strokeWidth={2} fill="url(#expGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// Loss Trend Chart (ECL + Write-offs)
function LossTrendChart({ data }) {
  if (!data) return null;
  return (
    <div className="h-[140px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: 0, right: 10, top: 5, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="date" tick={{ fontSize: 9 }} />
          <YAxis tickFormatter={(v) => `$${(v/1_000_000).toFixed(1)}M`} tick={{ fontSize: 9 }} />
          <RechartsTooltip formatter={(v, n) => [formatCurrency(v, false), n === "ecl" ? "ECL" : "Write-offs"]} contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 11 }} />
          <Line type="monotone" dataKey="ecl" name="ecl" stroke="#EA580C" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="net_writeoffs" name="net_writeoffs" stroke="#DC2626" strokeWidth={2} dot={false} strokeDasharray="4 2" />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-4 mt-1">
        <span className="text-[9px] text-[#64748B] flex items-center gap-1"><span className="w-3 h-0.5 bg-orange-500 rounded" />ECL</span>
        <span className="text-[9px] text-[#64748B] flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 rounded border-dashed" />Write-offs</span>
      </div>
    </div>
  );
}

// Worsening Accounts Table
function WorseningAccountsTable({ data, onRowClick }) {
  if (!data) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead className="bg-gray-50 border-b border-[#E2E8F0]">
          <tr>
            <th className="text-left p-2 font-medium text-[#502D3C]">Company</th>
            <th className="text-right p-2 font-medium text-[#502D3C]">Exposure</th>
            <th className="text-center p-2 font-medium text-[#502D3C]">Risk</th>
            <th className="text-right p-2 font-medium text-[#502D3C]">DPD</th>
            <th className="text-left p-2 font-medium text-[#502D3C]">Why Flagged</th>
          </tr>
        </thead>
        <tbody>
          {data.map((a) => (
            <tr key={a.company_id} onClick={() => onRowClick(a)} className="border-b border-[#E2E8F0] hover:bg-[#FFCC1C]/5 cursor-pointer">
              <td className="p-2">
                <p className="font-medium text-[#502D3C]">{a.company_name}</p>
                <p className="text-[9px] text-[#94A3B8]">{a.industry}</p>
              </td>
              <td className="p-2 text-right font-medium">{formatCurrency(a.exposure)}</td>
              <td className="p-2 text-center">
                <span className="inline-flex items-center gap-0.5 text-[10px]">
                  <Badge variant="outline" className="h-4 px-1" style={{ backgroundColor: RISK_CLASS_COLORS[a.risk_class_prior] + '20', borderColor: RISK_CLASS_COLORS[a.risk_class_prior] }}>{RISK_CLASS_LABELS[a.risk_class_prior]}</Badge>
                  <ChevronRight className="h-2.5 w-2.5 text-[#94A3B8]" />
                  <Badge variant="outline" className="h-4 px-1" style={{ backgroundColor: RISK_CLASS_COLORS[a.risk_class_current] + '20', borderColor: RISK_CLASS_COLORS[a.risk_class_current] }}>{RISK_CLASS_LABELS[a.risk_class_current]}</Badge>
                </span>
              </td>
              <td className="p-2 text-right text-red-600 font-medium">{a.days_past_due}d</td>
              <td className="p-2">
                <Badge variant="outline" className="text-[9px] h-4 bg-amber-50 text-amber-700 border-amber-200">{a.why_flagged}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Customer Concentration (mini)
function CustomerConcentrationChart({ data, onCustomerClick }) {
  if (!data) return null;
  return (
    <div className="space-y-1.5">
      {data.map((c) => (
        <button key={c.company_id} onClick={() => onCustomerClick(c)} className="w-full text-left hover:bg-gray-50 p-1.5 rounded transition-colors">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] font-medium text-[#502D3C] truncate">{c.company_name}</span>
            <span className="text-[9px] text-[#64748B]">{c.percentage}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${c.percentage * 3}%`, backgroundColor: RISK_CLASS_COLORS[c.risk_class] }} />
          </div>
        </button>
      ))}
    </div>
  );
}

// Industry Concentration (mini)
function IndustryConcentrationMini({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-1.5">
      {data.map((ind) => (
        <div key={ind.industry} className="flex items-center justify-between">
          <span className="text-[10px] text-[#502D3C]">{ind.industry}</span>
          <span className="text-[10px] text-[#64748B]">{formatCurrency(ind.exposure)} <span className="text-[#94A3B8]">({ind.percentage}%)</span></span>
        </div>
      ))}
    </div>
  );
}

// Metric Detail Content
function MetricDetailContent({ metric, isAnalystView }) {
  if (!metric) return null;
  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-xs font-semibold text-[#502D3C] mb-1.5">What is this?</h4>
        <p className="text-xs text-[#64748B] leading-relaxed">{metric.definition_long}</p>
      </div>
      <div>
        <h4 className="text-xs font-semibold text-[#502D3C] mb-1.5">Why it matters</h4>
        <p className="text-xs text-[#64748B] leading-relaxed">{metric.why_it_matters}</p>
      </div>
      <div>
        <h4 className="text-xs font-semibold text-[#502D3C] mb-1.5">Formula</h4>
        <p className="text-xs font-mono bg-gray-50 p-2.5 rounded border border-gray-100">{metric.formula_display}</p>
        {isAnalystView && <p className="text-[10px] font-mono text-[#94A3B8] mt-1.5 bg-gray-100 p-2 rounded">{metric.formula_logic}</p>}
      </div>
      <div>
        <h4 className="text-xs font-semibold text-[#502D3C] mb-1.5">Example</h4>
        <p className="text-xs text-[#64748B] bg-[#FFCC1C]/10 p-2.5 rounded border border-[#FFCC1C]/20">{metric.example}</p>
      </div>
      <Separator />
      <div>
        <h4 className="text-xs font-semibold text-[#502D3C] mb-2">Data Source</h4>
        <div className="space-y-1.5 text-xs bg-gray-50 p-3 rounded">
          <div className="flex justify-between"><span className="text-[#64748B]">System</span><span className="text-[#502D3C] font-medium">{metric.source_system}</span></div>
          <div className="flex justify-between"><span className="text-[#64748B]">Tables</span><span className="text-[#502D3C] font-mono text-[10px]">{metric.source_tables?.join(', ')}</span></div>
          <div className="flex justify-between"><span className="text-[#64748B]">Refresh</span><span className="text-[#502D3C]">{metric.refresh_cadence}</span></div>
          <div className="flex justify-between"><span className="text-[#64748B]">Owner</span><span className="text-[#502D3C]">{metric.data_owner}</span></div>
        </div>
      </div>
      {metric.rippling_advantage && (
        <div className="bg-[#502D3C]/5 p-3 rounded border border-[#502D3C]/10">
          <h4 className="text-xs font-semibold text-[#502D3C] mb-1.5 flex items-center gap-1.5">
            <span className="w-4 h-4 bg-[#FFCC1C] rounded flex items-center justify-center text-[#502D3C] text-[9px] font-bold">R</span>
            Why Rippling can see this
          </h4>
          <p className="text-xs text-[#64748B] leading-relaxed">{metric.rippling_advantage}</p>
        </div>
      )}
      {metric.caveats && (
        <div>
          <h4 className="text-xs font-semibold text-[#502D3C] mb-1.5">Caveats</h4>
          <p className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded border border-amber-100">{metric.caveats}</p>
        </div>
      )}
    </div>
  );
}

// Explain Content
function ExplainContent() {
  const steps = [
    { title: "Portfolio Size", desc: "Start with Gross On-Book Exposure — how much customers owe.", icon: "1" },
    { title: "What's Late", desc: "Past Due shows money already overdue. Lower is better.", icon: "2" },
    { title: "What's Due Soon", desc: "Due in Next 30 Days shows upcoming collections.", icon: "3" },
    { title: "Collections Health", desc: "On-Time Collection Rate shows payment reliability.", icon: "4" },
    { title: "Expected Loss", desc: "ECL predicts future losses from the current book.", icon: "5" },
    { title: "Realized Loss", desc: "Net Write-offs shows actual losses already taken.", icon: "6" },
  ];
  return (
    <div className="space-y-4">
      <p className="text-xs text-[#64748B]">This dashboard answers: How big is the portfolio? What's late? Are collections working? What losses do we expect?</p>
      {steps.map((s) => (
        <div key={s.title} className="flex gap-3 p-3 bg-gray-50 rounded">
          <span className="w-5 h-5 bg-[#FFCC1C] rounded-full flex items-center justify-center text-[10px] font-bold text-[#502D3C] flex-shrink-0">{s.icon}</span>
          <div>
            <h4 className="font-medium text-[#502D3C] text-sm">{s.title}</h4>
            <p className="text-xs text-[#64748B] mt-0.5">{s.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// Customer Detail Content
function CustomerDetailContent({ customer }) {
  if (!customer) return null;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-gray-50 rounded">
          <p className="text-[10px] text-[#64748B]">Exposure</p>
          <p className="text-lg font-bold text-[#502D3C]">{formatCurrency(customer.exposure)}</p>
        </div>
        <div className="p-3 bg-gray-50 rounded">
          <p className="text-[10px] text-[#64748B]">Risk Class</p>
          <div className="flex items-center gap-1.5">
            <Badge style={{ backgroundColor: RISK_CLASS_COLORS[customer.risk_class_current] + '20' }}>{RISK_CLASS_LABELS[customer.risk_class_current]}</Badge>
            {customer.risk_class_prior && <span className="text-[10px] text-[#64748B]">from {RISK_CLASS_LABELS[customer.risk_class_prior]}</span>}
          </div>
        </div>
      </div>
      {customer.why_flagged && (
        <div className="p-3 bg-amber-50 rounded border border-amber-200">
          <h4 className="font-medium text-amber-800 text-xs mb-1">Why Flagged</h4>
          <Badge variant="outline" className="bg-white text-amber-700 border-amber-300">{customer.why_flagged}</Badge>
        </div>
      )}
      {customer.narrative && (
        <div className="p-3 bg-[#502D3C]/5 rounded">
          <h4 className="font-medium text-[#502D3C] text-xs mb-1">Summary</h4>
          <p className="text-xs text-[#64748B] leading-relaxed">{customer.narrative}</p>
        </div>
      )}
      {customer.days_past_due !== undefined && (
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-red-50 rounded">
            <p className="text-[10px] text-red-600">Days Past Due</p>
            <p className="text-lg font-bold text-red-700">{customer.days_past_due}d</p>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <p className="text-[10px] text-[#64748B]">Industry</p>
            <p className="text-sm font-medium text-[#502D3C]">{customer.industry}</p>
          </div>
        </div>
      )}
    </div>
  );
}
